import { firstValueFrom } from "rxjs";
import { Injectable } from "@snap/ts-inject";
import { lensCoreFactory } from "../lens-core-module/loader/lensCoreFactory";
import type { LensCore } from "../lens-core-module/lensCore";
import { getLogger } from "../logger/logger";
import type { RemoteConfiguration } from "./remoteConfiguration";
import { remoteConfigurationFactory } from "./remoteConfiguration";
import { toLensCoreConfigValue } from "./util";

const logger = getLogger("registerConfigurationProvider");

/**
 * Registers an on-demand configuration provider with LensCore.
 *
 * When LensCore needs a config that was not preloaded, it calls this provider.
 * The provider looks up the config by ID from the RemoteConfiguration cache
 * (already in JS memory from the COF fetch), so no additional network requests are made.
 */
export const registerConfigurationProvider = Injectable(
    "registerConfigurationProvider",
    [lensCoreFactory.token, remoteConfigurationFactory.token] as const,
    (lensCore: LensCore, remoteConfiguration: RemoteConfiguration) => {
        if (!lensCore.setConfigurationProvider) {
            return logger.warn("LensCore version does not support on-demand configuration.");
        }

        lensCore.setConfigurationProvider(async ({ configId }) => {
            try {
                const configs = await firstValueFrom(remoteConfiguration.get(configId));
                if (configs.length === 0) return undefined;

                const { configId: id, value } = configs[0];
                if (!value) return undefined;

                const convertedValue = toLensCoreConfigValue(value);
                if (!convertedValue) return undefined;

                return { configId: id, value: convertedValue };
            } catch (error) {
                logger.error(new Error(`Failed to resolve config "${configId}" on demand.`, { cause: error }));
                return undefined;
            }
        });
    }
);
